import React, { useEffect, useRef, useState } from "react";
import { Paper, Tab, Stack } from "@mui/material";
import { TabContext } from "@mui/lab";
import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { _roundToDecimals } from "../../../utils/_roundToDecimals";
import { precision } from "../../../_utils/precision";
import { TabsStyled } from "../../../_components/TabsStyled";
import { TabPanelStyled } from "../../../_components/TabPanelStyled";
import { TextFieldStyled } from "../../../_components/TextFieldStyled";
import AdornmentQuoteIcon from "./AdornmentQuoteIcon";
import AdronTypography from "../../../_components/AdronTypography";
import { NumericFormatCustom } from "../../../components/ui/StyledAdron";
import { Bot, Direction, RangeStart } from "../../../_app/types";

interface Props {
  bot: Bot;
  onChangeMinPrice: (value: number) => void;
  onChangeMaxPrice: (value: number) => void;
  onChangeStartPrice: (value: number) => void;
  onChangePriceStep: (value: number) => void;
  minPrice: number;
  maxPrice: number;
  startPrice: number;
  gridDirection?: Direction;
  orderStep?: number;
  rangeStartMode: RangeStart;
  switchRangeStartMode: (value: RangeStart) => void;
  onEdit: (value: boolean) => void;
}

const FuturesBotRangePrice = ({
  bot,
  onChangeMinPrice,
  onChangeMaxPrice,
  onChangeStartPrice,
  maxPrice,
  minPrice,
  startPrice,
  onChangePriceStep,
  rangeStartMode,
  switchRangeStartMode,
  onEdit,
}: Props) => {
  const theme = useTheme();
  const textFieldRef = useRef<HTMLDivElement | HTMLInputElement>(null);
  const CustomGridMode = useSelector((state: RootState) => state.customGridMode);
  const handleChangeTab = (event: React.SyntheticEvent, newValue: RangeStart) =>
    switchRangeStartMode(newValue);

  const [stringMinPrice, setStringMinPrice] = useState<string>(String(minPrice));
  const [stringMaxPrice, setStringMaxPrice] = useState<string>(String(maxPrice));
  const [stringStartPrice, setStringStartPrice] = useState<string>(String(startPrice));

  useEffect(() => {
    if (!minPrice) return;
    setStringMinPrice(String(minPrice));
  }, [minPrice]);

  useEffect(() => {
    if (!maxPrice) return;
    setStringMaxPrice(String(maxPrice));
  }, [maxPrice]);

  useEffect(() => {
    if (!startPrice) return;
    setStringStartPrice(String(startPrice));
  }, [startPrice]);

  // useEffect(() => {
  // 	onChangeMinPrice(Number(stringMinPrice));
  // }, [stringMinPrice]);
  //
  // useEffect(() => {
  // 	onChangeMaxPrice(Number(stringMaxPrice));
  // }, [stringMaxPrice]);
  //
  // useEffect(() => {
  // 	onChangeStartPrice(_roundToDecimals(Number(stringStartPrice), precision(bot.ticker.priceStep)));
  // }, [stringStartPrice]);

  // useEffect(() => {
  // 	setStringMaxPrice(String(maxPrice));
  // 	console.log('setStringMaxPrice(String(maxPrice))', maxPrice);
  // }, [maxPrice]);

  // useEffect(() => {
  // 	setStringStartPrice(String(startPrice));
  // }, [startPrice]);

  const helperText = "Please fill out this field";

  const handleMinPrice = (value: string) => {
    setStringMinPrice(value);
    onChangeMinPrice(Number(value));
  };
  const handleMaxPrice = (value: string) => {
    setStringMaxPrice(value);
    onChangeMaxPrice(Number(value));
  };
  const handleStartPrice = (value: string) => {
    setStringStartPrice(value);
    onChangeStartPrice(Number(value));
  };

  const handleMinPriceOnBlur = (minInput: number) => {
    let min = minInput <= 0 ? bot.ticker.priceStep : minInput;
    min = _roundToDecimals(min, precision(bot.ticker.priceStep));
    onChangeMinPrice(min);

    if (bot.gridType === "ARITHMETIC") {
      const priceStep = Math.abs(maxPrice - min) / bot.gridsAmount;
      switch (bot.gridDirection) {
        case "Central":
          let startPrice = min + (bot.gridsAmount / 2) * priceStep;
          onChangeStartPrice(startPrice);
          onChangePriceStep(priceStep);
          break;
        case "Long":
          onChangeStartPrice(maxPrice);
          onChangePriceStep(priceStep);
          break;
        case "Short":
          onChangeStartPrice(min);
          onChangePriceStep(priceStep);
          break;
        default:
          break;
      }
    } else if (bot.gridType === "GEOMETRIC") {
      let rangeDivision = Math.abs(maxPrice / min);
      let pow = 2 / bot.gridsAmount;
      const priceStep = (Math.pow(rangeDivision, pow) - 1) / (1 + Math.pow(rangeDivision, pow));
      switch (bot.gridDirection) {
        case "Central":
          onChangeStartPrice(min * Math.pow(1 + priceStep, bot.gridsAmount / 2));
          onChangePriceStep(priceStep);
          break;
        case "Long":
          onChangeStartPrice(maxPrice);
          onChangePriceStep(priceStep);
          break;
        case "Short":
          onChangeStartPrice(min);
          onChangePriceStep(priceStep);
          break;
        default:
          break;
      }
    }
  };

  const handleMaxPriceOnBlur = (maxInput: number) => {
    let max = maxInput <= 0 ? bot.ticker.priceStep : maxInput;
    max = _roundToDecimals(max, precision(bot.ticker.priceStep));
    onChangeMaxPrice(max);

    if (bot.gridType === "ARITHMETIC") {
      const priceStep = Math.abs(max - minPrice) / bot.gridsAmount;
      switch (bot.gridDirection) {
        case "Central":
          let startPrice = max - (bot.gridsAmount / 2) * priceStep;
          onChangeStartPrice(startPrice);
          onChangePriceStep(priceStep);
          break;
        case "Long":
          onChangeStartPrice(max);
          onChangePriceStep(priceStep);
          break;
        case "Short":
          onChangeStartPrice(minPrice);
          onChangePriceStep(priceStep);
          break;
        default:
          break;
      }
    } else if (bot.gridType === "GEOMETRIC") {
      let rangeDivision = Math.abs(max / minPrice);
      let pow = 2 / bot.gridsAmount;
      const priceStep = (Math.pow(rangeDivision, pow) - 1) / (1 + Math.pow(rangeDivision, pow));
      switch (bot.gridDirection) {
        case "Central":
          onChangeStartPrice(max * Math.pow(1 - priceStep, bot.gridsAmount / 2));
          onChangePriceStep(priceStep);
          break;
        case "Long":
          onChangeStartPrice(max);
          onChangePriceStep(priceStep);
          break;
        case "Short":
          onChangeStartPrice(minPrice);
          onChangePriceStep(priceStep);
          break;
        default:
          break;
      }
    }
  };

  const handleStartPriceOnBlur = (startInput: number) => {
    let start = startInput <= 0 ? bot.ticker.priceStep : startInput;
    start = _roundToDecimals(start, precision(bot.ticker.priceStep));
    onChangeStartPrice(start);

    onEdit(false);
    if (bot.gridType === "ARITHMETIC") {
      switch (bot.gridDirection) {
        case "Central":
          let min = start - (bot.gridsAmount / 2) * bot.priceStep;
          let max = start + (bot.gridsAmount / 2) * bot.priceStep;
          onChangeMinPrice(start - (bot.gridsAmount / 2) * bot.priceStep);
          setStringMinPrice(String(min));
          onChangeMaxPrice(start + (bot.gridsAmount / 2) * bot.priceStep);
          setStringMaxPrice(String(max));
          break;
        case "Long":
          onChangeMinPrice(start - bot.gridsAmount * bot.priceStep);
          setStringMinPrice(String(String(start - bot.gridsAmount * bot.priceStep)));
          onChangeMaxPrice(start);
          setStringMaxPrice(String(start));
          break;
        case "Short":
          onChangeMinPrice(start);
          setStringMinPrice(String(start));
          onChangeMaxPrice(start + bot.gridsAmount * bot.priceStep);
          setStringMaxPrice(String(start + bot.gridsAmount * bot.priceStep));
          break;
        default:
          break;
      }
    } else if (bot.gridType === "GEOMETRIC") {
      switch (bot.gridDirection) {
        case "Central":
          let minCentral = bot.startPrice / Math.pow(1 + bot.priceStep, bot.gridsAmount / 2);
          let maxCentral = bot.startPrice / Math.pow(1 - bot.priceStep, bot.gridsAmount / 2);

          onChangeMinPrice(minCentral);
          onChangeMaxPrice(maxCentral);
          setStringMinPrice(String(minCentral));
          setStringMaxPrice(String(maxCentral));
          break;
        case "Long":
          let minLong = bot.startPrice * Math.pow(1 + bot.priceStep, bot.gridsAmount);
          let maxLong = start;

          onChangeMinPrice(minLong);
          onChangeMaxPrice(maxLong);
          setStringMinPrice(String(minLong));
          setStringMaxPrice(String(maxLong));
          break;
        case "Short":
          let minShort = start;
          let maxShort = bot.startPrice / Math.pow(1 - bot.priceStep, bot.gridsAmount);

          onChangeMinPrice(minShort);
          onChangeMaxPrice(maxShort);
          setStringMinPrice(String(minShort));
          setStringMaxPrice(String(maxShort));
          break;
        default:
          break;
      }
    }
  };

  return (
    <Paper
      elevation={0}
      className="base-adron-paper paper-light"
      style={{ backgroundColor: theme.palette.grey[400] }}
    >
      <TabContext value={rangeStartMode}>
        <TabsStyled style={{ borderRadius: 12 }} onChange={handleChangeTab}>
          <Tab
            disabled={CustomGridMode}
            label="Range"
            value={"range"}
            className="AdronSecondaryTabs"
          />
          <Tab
            disabled={CustomGridMode}
            label="Start price"
            value={"start"}
            className="AdronSecondaryTabs"
          />
        </TabsStyled>
        <TabPanelStyled noPadding={true} value="range">
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            sx={{ width: "100%" }}
            spacing={1}
          >
            <TextFieldStyled
              disabled={CustomGridMode}
              className={`textAlignRight AdronRangePrice ${
                theme.palette.mode === "dark" ? "MuiFormHelperTextDARK" : "MuiFormHelperTextLIGHT"
              }`}
              helperText={(stringMinPrice === "" || Number(stringMinPrice) === 0) && helperText}
              fullWidth={true}
              label="Low price"
              variant="outlined"
              value={stringMinPrice}
              error={stringMinPrice === "" || Number(stringMinPrice) === 0}
              size="small"
              autoComplete={"off"}
              onChange={(e) => handleMinPrice(e.target.value)}
              onBlur={() => handleMinPriceOnBlur(minPrice)}
              InputProps={{
                startAdornment: <AdornmentQuoteIcon quoteCurrencyName={bot.quote} />,
                endAdornment: (
                  <AdronTypography
                    style={{ marginLeft: "6px" }}
                    halfOpacity
                    value={`${bot.quote}`}
                  />
                ),
                inputComponent: NumericFormatCustom as any,
                inputProps: {
                  inputMode: "decimal",
                  pattern: "[0-9]*",
                  decimalCustom: Math.abs(Math.log10(bot.ticker.priceStep)),
                },
              }}
            />
            <TextFieldStyled
              disabled={CustomGridMode}
              className={`textAlignRight AdronRangePrice ${
                theme.palette.mode === "dark" ? "MuiFormHelperTextDARK" : "MuiFormHelperTextLIGHT"
              }`}
              helperText={(stringMaxPrice === "" || Number(stringMaxPrice) === 0) && helperText}
              fullWidth={true}
              label="High price"
              variant="outlined"
              value={stringMaxPrice}
              error={stringMaxPrice === "" || Number(stringMaxPrice) === 0}
              size="small"
              autoComplete={"off"}
              onChange={(e) => handleMaxPrice(e.target.value)}
              onBlur={() => handleMaxPriceOnBlur(maxPrice)}
              InputProps={{
                startAdornment: <AdornmentQuoteIcon quoteCurrencyName={bot.quote} />,
                endAdornment: (
                  <AdronTypography
                    style={{ marginLeft: "6px" }}
                    halfOpacity
                    value={`${bot.quote}`}
                  />
                ),
                inputComponent: NumericFormatCustom as any,
                inputProps: {
                  inputMode: "decimal",
                  pattern: "[0-9]*",
                  decimalCustom: Math.abs(Math.log10(bot.ticker.priceStep)),
                },
              }}
            />
            {/*<TextFieldStyled*/}
            {/*	disabled={CustomGridMode}*/}
            {/*	className={`textAlignRight AdronRangePrice ${theme.palette.mode === 'dark' ? "MuiFormHelperTextDARK" : "MuiFormHelperTextLIGHT"}`}*/}
            {/*	fullWidth={true}*/}
            {/*	label="High price"*/}
            {/*	variant="outlined"*/}
            {/*	helperText={maxPrice === 0 && helperText}*/}
            {/*	value={_roundToDecimals(maxPrice, precision(bot.ticker.priceStep)) === 0*/}
            {/*		? ''*/}
            {/*		: _roundToDecimals(maxPrice, precision(bot.ticker.priceStep))*/}
            {/*	}*/}
            {/*	size="small"*/}
            {/*	onChange={e => handleMaxPrice(e.target.value === '' ? 0 : +e.target.value)}*/}
            {/*	onBlur={() => handleMaxPriceOnBlur(maxPrice)}*/}
            {/*	type={'text'}*/}
            {/*	error={maxPrice === 0}*/}
            {/*	autoComplete={"off"}*/}
            {/*	InputProps={{*/}
            {/*		style: {textAlign: 'right'},*/}
            {/*		startAdornment: (*/}
            {/*			<InputAdornment position="start">*/}
            {/*				<TetherIcon/>*/}
            {/*			</InputAdornment>*/}
            {/*		),*/}
            {/*		endAdornment: (*/}
            {/*			<AdronTypography style={{marginLeft: "6px"}} halfOpacity value={`${bot.quote}`}/>*/}
            {/*		),*/}
            {/*		inputComponent: NumericFormatCustom as any,*/}
            {/*		inputProps: {*/}
            {/*			inputMode: 'decimal',*/}
            {/*			pattern: '[0-9]*',*/}
            {/*			decimalCustom: Math.abs(Math.log10(bot.ticker.priceStep)),*/}
            {/*		}*/}
            {/*	}}*/}
            {/*/>*/}
          </Stack>
        </TabPanelStyled>
        <TabPanelStyled noPadding={true} value="start">
          <TextFieldStyled
            disabled={CustomGridMode}
            className={`textAlignRight AdronRangePrice ${
              theme.palette.mode === "dark" ? "MuiFormHelperTextDARK" : "MuiFormHelperTextLIGHT"
            }`}
            helperText={(stringStartPrice === "" || Number(stringStartPrice) === 0) && helperText}
            fullWidth={true}
            label="High price"
            variant="outlined"
            value={stringStartPrice}
            error={stringStartPrice === "" || Number(stringStartPrice) === 0}
            size="small"
            autoComplete={"off"}
            onChange={(e) => {
              handleStartPrice(e.target.value);
            }}
            onBlur={() => handleStartPriceOnBlur(Number(stringStartPrice))}
            InputProps={{
              startAdornment: <AdornmentQuoteIcon quoteCurrencyName={bot.quote} />,
              endAdornment: (
                <AdronTypography style={{ marginLeft: "6px" }} halfOpacity value={`${bot.quote}`} />
              ),
              inputComponent: NumericFormatCustom as any,
              inputProps: {
                inputMode: "decimal",
                pattern: "[0-9]*",
                decimalCustom: Math.abs(Math.log10(bot.ticker.priceStep)),
              },
            }}
          />
        </TabPanelStyled>
      </TabContext>
      <div className={"textFieldRef"} ref={textFieldRef} />
    </Paper>
  );
};

export default FuturesBotRangePrice;
